import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { createAsset } from '../../api/assets';
import { extractErrorMessage } from '../../api/client';
import AssetFormInner from './AssetFormInner';

const EMPTY = {
  name: '',
  category: '',
  serialNumber: '',
  assignedTo: '',
  purchaseCost: '',
  purchaseDate: '',
  warrantyExpiry: '',
};

export default function AssetForm({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const qc = useQueryClient();

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setErrors({});
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: createAsset,
    onSuccess: () => {
      toast.success('Asset registered');
      qc.invalidateQueries({ queryKey: ['assets'] });
      onClose?.();
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err));
    },
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.category.trim()) e.category = 'Required';
    if (!form.serialNumber.trim()) e.serialNumber = 'Required';
    if (form.purchaseCost === '' || Number(form.purchaseCost) < 0)
      e.purchaseCost = 'Must be 0 or greater';
    if (!form.purchaseDate) e.purchaseDate = 'Required';
    if (!form.warrantyExpiry) e.warrantyExpiry = 'Required';
    if (
      form.purchaseDate &&
      form.warrantyExpiry &&
      form.warrantyExpiry < form.purchaseDate
    ) {
      e.warrantyExpiry = 'Must be on or after purchase date';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const payload = {
      name: form.name.trim(),
      category: form.category.trim(),
      serialNumber: form.serialNumber.trim(),
      purchaseCost: Number(form.purchaseCost),
      purchaseDate: form.purchaseDate,
      warrantyExpiry: form.warrantyExpiry,
    };
    if (form.assignedTo) payload.assignedTo = Number(form.assignedTo);
    mutation.mutate(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Register Asset"
      description="Add a new hardware or equipment record."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={mutation.isPending}
          >
            Register
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {mutation.isError && <ErrorBanner error={mutation.error} />}

        <AssetFormInner
          form={form}
          update={(patch) => setForm((p) => ({ ...p, ...patch }))}
          errors={errors}
        />
      </form>
    </Modal>
  );
}
